// 세금계산서 업로드 생성·검증 데이터 소스 (C420).
//
// apiClient는 prefixUrl을 쓰므로 경로 앞에 '/'를 붙이지 않는다.

import { apiClient } from '@/lib/api-client';
import type {
  IssuedTaxInvoiceRow,
  OrphanCreateRequest,
  OrphanCreateResult,
  PetrohqApiResponseDeleteResult,
  PetrohqApiResponseListIssuedTaxInvoiceRow,
  PetrohqApiResponseOrphanCreateResult,
  PetrohqApiResponseTaxInvoiceDocument,
  PetrohqApiResponseUploadGeneratedResult,
  PetrohqApiResponseUploadRowsResponse,
  PetrohqApiResponseVerifyResponse,
  TaxInvoiceDocument,
  UploadGeneratedRequest,
  UploadGeneratedResult,
  UploadRowsResponse,
  VerifyRequest,
  VerifyResponse,
} from '@/lib/api/data-contracts';

export interface IssuedParams {
  storeCd: string;
  startDate: string;
  endDate: string;
  dealCode?: string;
}

function toSearchParams(params: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.append(key, value);
    }
  }
  return search;
}

/**
 * 발행내역. verifyStatus는 저장된 승인번호에서 파생하므로 matched/unmatched 2종뿐이다 —
 * mismatch·canceled는 verify 응답에만 있다.
 */
export async function fetchIssuedTaxInvoices(
  params: IssuedParams
): Promise<IssuedTaxInvoiceRow[]> {
  const response = await apiClient.get('tax-invoices/issued', {
    searchParams: toSearchParams({ ...params }),
  });
  const data =
    (await response.json()) as PetrohqApiResponseListIssuedTaxInvoiceRow;
  return data.result ?? [];
}

/**
 * 업로드 양식 59컬럼 행. 승인 완료 문서는 제외되고, 남은 대상이 100건을 넘으면
 * 작성일자·taxNo 순으로 앞 100건만 담긴다.
 */
export async function fetchUploadRows(
  storeCd: string,
  taxNos: number[]
): Promise<UploadRowsResponse> {
  const searchParams = new URLSearchParams({ storeCd });
  taxNos.forEach(taxNo => searchParams.append('taxNos', String(taxNo)));
  const response = await apiClient.get('tax-invoices/upload-rows', {
    searchParams,
  });
  const data = (await response.json()) as PetrohqApiResponseUploadRowsResponse;
  return data.result ?? {};
}

/** 파일 저장에 성공한 뒤에만 호출한다. 생성횟수가 1 올라간다 */
export async function reportUploadGenerated(
  request: UploadGeneratedRequest
): Promise<UploadGeneratedResult | undefined> {
  const response = await apiClient.post('tax-invoices/upload-generated', {
    json: request,
  });
  const data =
    (await response.json()) as PetrohqApiResponseUploadGeneratedResult;
  return data.result;
}

/** 대사. 재실행마다 파괴적이므로(판정이 matched가 아니면 저장된 승인번호·전송일자를 지운다) 호출 전 확인을 받는다 */
export async function verifyTaxInvoices(
  request: VerifyRequest
): Promise<VerifyResponse> {
  const response = await apiClient.post('tax-invoices/verify', { json: request });
  const data = (await response.json()) as PetrohqApiResponseVerifyResponse;
  return data.result ?? {};
}

/** verify가 준 orphanCandidates를 사용자 확인 후 등록한다 */
export async function createOrphans(
  request: OrphanCreateRequest
): Promise<OrphanCreateResult | undefined> {
  const response = await apiClient.post('tax-invoices/orphans', { json: request });
  const data = (await response.json()) as PetrohqApiResponseOrphanCreateResult;
  return data.result;
}

/** 생성 취소. 생성횟수가 1 이상이면 서버가 차단한다 */
export async function deleteTaxInvoice(taxNo: number): Promise<number | undefined> {
  const response = await apiClient.delete(`tax-invoices/${taxNo}`);
  const data = (await response.json()) as PetrohqApiResponseDeleteResult;
  return data.result?.deletedTaxNo;
}

/** 인쇄용 확정 데이터. 미등록문서는 buyer가 null일 수 있다 */
export async function fetchTaxInvoiceDocument(
  taxNo: number
): Promise<TaxInvoiceDocument> {
  const response = await apiClient.get(`tax-invoices/${taxNo}/document`);
  const data = (await response.json()) as PetrohqApiResponseTaxInvoiceDocument;
  if (!data.result) {
    throw new Error(`세금계산서 자료를 찾을 수 없습니다: ${taxNo}`);
  }
  return data.result;
}
